import { FC, useEffect, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Input, Button } from '../../components/Form';
import { EReleaseNotesContentType, ReleaseNotesItemSelector } from './ReleaseNotesItemSelector.tsx';
import { IGetReleaseNote, IUpdateReleaseNote, TReleaseNoteItem } from '../../api/api';
import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/skins/ui/oxide/skin.css';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/quickbars';
import 'tinymce/skins/content/default/content.css';

interface ReleaseNotesContentItemLocal extends TReleaseNoteItem {
  id: string;
}

export interface ReleaseNotesFormData extends IUpdateReleaseNote {}

interface ReleaseNotesFormProps {
  releaseNote?: IGetReleaseNote;
  onSubmit: (data: ReleaseNotesFormData) => void;
  onCancel: () => void;
  onDelete: () => void;
}

const mapItemsToLocalState = (items?: TReleaseNoteItem[]): ReleaseNotesContentItemLocal[] =>
  (items ?? []).map((item, index) => ({
    ...item,
    id: `${item.position}-${index}`,
  }));

const formatLocalDateInputValue = (publicationAt?: string) => {
  if (!publicationAt) {
    return '';
  }

  const date = new Date(publicationAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatLocalTimeInputValue = (publicationAt?: string) => {
  if (!publicationAt) {
    return '';
  }

  const date = new Date(publicationAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const ReleaseNotesForm: FC<ReleaseNotesFormProps> = ({ releaseNote, onSubmit, onCancel, onDelete }) => {
  const [formData, setFormData] = useState<ReleaseNotesFormData>(() => ({
    title: releaseNote?.title || '',
    publication_at: releaseNote?.publication_at || '',
    items: releaseNote?.items || [],
  }));
  const [publicationDate, setPublicationDate] = useState(() =>
    formatLocalDateInputValue(releaseNote?.publication_at)
  );
  const [publicationTime, setPublicationTime] = useState(() =>
    formatLocalTimeInputValue(releaseNote?.publication_at)
  );
  const [contentItems, setContentItems] = useState<ReleaseNotesContentItemLocal[]>(() =>
    mapItemsToLocalState(releaseNote?.items)
  );

  useEffect(() => {
    setFormData({
      title: releaseNote?.title || '',
      publication_at: releaseNote?.publication_at || '',
      items: releaseNote?.items || [],
    });
    setPublicationDate(formatLocalDateInputValue(releaseNote?.publication_at));
    setPublicationTime(formatLocalTimeInputValue(releaseNote?.publication_at));
    setContentItems(mapItemsToLocalState(releaseNote?.items));
  }, [releaseNote]);

  const syncFormItems = (items: ReleaseNotesContentItemLocal[]) => {
    setContentItems(items);
    setFormData((prev) => ({
      ...prev,
      items: items.map((item, index) => ({
        position: index,
        type: item.type,
        content: item.content,
      })),
    }));
  };

  const updatePublicationAt = (date: string, time: string) => {
    const publicationAt = date && time ? new Date(`${date}T${time}`).toISOString() : '';
    setFormData((prev) => ({
      ...prev,
      publication_at: publicationAt,
    }));
  };

  const createContentItem = (type: EReleaseNotesContentType): ReleaseNotesContentItemLocal | null => {
    if (type !== EReleaseNotesContentType.TEXT) {
      return null;
    }

    return {
      id: String(Date.now()),
      position: 0,
      type: EReleaseNotesContentType.TEXT,
      content: '',
    };
  };

  const addContentItemAt = (type: EReleaseNotesContentType, index: number) => {
    const newItem = createContentItem(type);
    if (!newItem) {
      return;
    }

    const nextItems = [
      ...contentItems.slice(0, index),
      newItem,
      ...contentItems.slice(index),
    ];
    syncFormItems(nextItems);
  };

  const updateTextContentItem = (id: string, content: string) => {
    syncFormItems(
      contentItems.map((item) =>
        item.id === id && item.type === EReleaseNotesContentType.TEXT
          ? { ...item, content }
          : item
      )
    );
  };

  const moveContentItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= contentItems.length) {
      return;
    }

    const nextItems = [...contentItems];
    const [movedItem] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, movedItem);
    syncFormItems(nextItems);
  };

  const removeContentItem = (id: string) => {
    syncFormItems(contentItems.filter((item) => item.id !== id));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Date"
        type="date"
        value={publicationDate}
        onChange={(e) => {
          const nextDate = e.target.value;
          setPublicationDate(nextDate);
          updatePublicationAt(nextDate, publicationTime);
        }}
        onClick={(e) => (e.target as HTMLInputElement).showPicker()}
        required
        className="cursor-pointer"
      />
      <Input
        label="Time"
        type="time"
        value={publicationTime}
        onChange={(e) => {
          const nextTime = e.target.value;
          setPublicationTime(nextTime);
          updatePublicationAt(publicationDate, nextTime);
        }}
        onClick={(e) => (e.target as HTMLInputElement).showPicker()}
        required
        className="cursor-pointer"
      />
      <Input
        label="Title"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        required
      />
      <ReleaseNotesItemSelector
        contentTypes={[EReleaseNotesContentType.TEXT]}
        onSelect={(type) => addContentItemAt(type, 0)}
      />
      {contentItems.map((item, index) => (
        <div key={item.id} className="space-y-4">
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-end gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Move content up"
                  disabled={index === 0}
                  onClick={() => moveContentItem(index, index - 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                    <path d="M18 15l-6-6-6 6" />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Move content down"
                  disabled={index === contentItems.length - 1}
                  onClick={() => moveContentItem(index, index + 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Delete content"
                  onClick={() => removeContentItem(item.id)}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-white text-red-600 transition-colors hover:bg-red-50"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                    <path d="M3 6h18" />
                    <path d="M8 6V4h8v2" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                </button>
              </div>
            </div>
            {item.type === EReleaseNotesContentType.TEXT ? (
              <Editor
                value={item.content}
                onEditorChange={(content) => updateTextContentItem(item.id, content)}
                tinymceScriptSrc={undefined}
                licenseKey="gpl"
                init={{
                  min_height: 50,
                  menubar: false,
                  statusbar: false,
                  toolbar: false,
                  plugins: [
                    'autolink',
                    'autoresize',
                    'lists',
                    'link',
                    'quickbars',
                  ],
                  autoresize_bottom_margin: 0,
                  quickbars_selection_toolbar:
                    'bold italic underline strikethrough | bullist numlist | quicklink removeformat',
                  quickbars_insert_toolbar: false,
                  branding: false,
                  promotion: false,
                  content_style:
                    'body { margin: 8px 12px; overflow-y: hidden; } p { margin: 0; }',
                }}
              />
            ) : null}
          </div>
          <ReleaseNotesItemSelector
            contentTypes={[EReleaseNotesContentType.TEXT]}
            onSelect={(type) => addContentItemAt(type, index + 1)}
          />
        </div>
      ))}

      <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
        <Button type="button" onClick={onDelete} className="bg-red-600">Delete</Button>

        <Button type="button" onClick={onCancel} className="bg-gray-600">
          Cancel
        </Button>
        <Button type="submit">{releaseNote ? 'Update' : 'Create'}</Button>
      </div>
    </form>
  );
};
